// Helper per pushare singoli record al cloud (best-effort, fire-and-forget)
import type { Note, PersonalTask, ProfitTask, Reminder } from '@/types';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { useAuthStore } from './authStore';

function getUser() {
  return useAuthStore.getState().user;
}

export async function pushNoteToCloud(note: Note): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('notes').upsert({
      id: note.id, user_id: user.id,
      title: note.title, content: note.content, color: note.color,
      tags: note.tags as any, is_pinned: note.is_pinned, is_archived: note.is_archived,
      reminder_id: note.reminder_id, created_at: note.created_at, updated_at: note.updated_at,
    }, { onConflict: 'id' });
  } catch (e) {
    console.warn('[sync] pushNote fallito', e);
  }
}

export async function deleteNoteFromCloud(id: string): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('notes').delete().eq('id', id).eq('user_id', user.id);
  } catch (e) {
    console.warn('[sync] deleteNote fallito', e);
  }
}

export async function pushPersonalToCloud(task: PersonalTask): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('personal_tasks').upsert({
      id: task.id, user_id: user.id,
      title: task.title, description: task.description, status: task.status, priority: task.priority,
      due_date: task.due_date, tags: task.tags as any, position: task.position,
      created_at: task.created_at, updated_at: task.updated_at,
    }, { onConflict: 'id' });
  } catch (e) {
    console.warn('[sync] pushPersonal fallito', e);
  }
}

export async function deletePersonalFromCloud(id: string): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('personal_tasks').delete().eq('id', id).eq('user_id', user.id);
  } catch (e) {
    console.warn('[sync] deletePersonal fallito', e);
  }
}

export async function pushProfitToCloud(task: ProfitTask): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('profit_tasks').upsert({
      id: task.id, user_id: user.id,
      title: task.title, description: task.description, status: task.status, priority: task.priority,
      client_name: task.client_name, expected_revenue: task.expected_revenue,
      actual_revenue: task.actual_revenue, cost: task.cost, currency: task.currency,
      due_date: task.due_date, tags: task.tags as any, position: task.position,
      created_at: task.created_at, updated_at: task.updated_at,
    }, { onConflict: 'id' });
  } catch (e) {
    console.warn('[sync] pushProfit fallito', e);
  }
}

export async function deleteProfitFromCloud(id: string): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('profit_tasks').delete().eq('id', id).eq('user_id', user.id);
  } catch (e) {
    console.warn('[sync] deleteProfit fallito', e);
  }
}

export async function pushReminderToCloud(r: Reminder): Promise<void> {
  const user = getUser();
  if (!user || !isSupabaseConfigured()) return;
  try {
    const c = getSupabase();
    await c.from('reminders').upsert({
      id: r.id, user_id: user.id, note_id: r.note_id,
      title: r.title, body: r.body, time: r.time,
      days_of_week: r.days_of_week as any, at: r.at, enabled: r.enabled,
      created_at: r.created_at, updated_at: r.updated_at,
    }, { onConflict: 'id' });
  } catch (e) {
    console.warn('[sync] pushReminder fallito', e);
  }
}
